import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import { logger } from '../../utils/logger';
import { analytics } from '../../utils/analytics';
import type { RootState } from '../../store/store';
import type { UploadedFile } from '../../api/types';

interface UploadQueueItem {
  id: string;
  file: { uri: string; name: string; type: string };
  description?: string;
  status: 'pending' | 'uploading' | 'analyzing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  result?: UploadedFile;
}

interface UploadState {
  files: UploadedFile[];
  uploadQueue: UploadQueueItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: UploadState = {
  files: [],
  uploadQueue: [],
  isLoading: false,
  error: null,
};

// Generate unique ID for queue items
const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

// Async thunks
export const addToUploadQueue = createAsyncThunk(
  'upload/addToUploadQueue',
  async (
    { file, description }: { file: { uri: string; name: string; type: string }; description?: string },
    { dispatch, getState }
  ) => {
    const id = generateId();
    const queueItem: UploadQueueItem = {
      id,
      file,
      description,
      status: 'pending',
      progress: 0,
    };

    // Track upload start
    analytics.trackFileUpload(file.type, !!description);

    // Add to queue first
    dispatch(addQueueItem(queueItem));

    // Start processing immediately
    dispatch(processQueueItem(id));

    return queueItem;
  }
);

export const processQueueItem = createAsyncThunk(
  'upload/processQueueItem',
  async (queueId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const queueItem = state.upload.uploadQueue.find(item => item.id === queueId);
    
    if (!queueItem) {
      throw new Error('Queue item not found');
    }

    try {
      const startTime = Date.now();
      
      // Update status to uploading
      dispatch(updateQueueItemStatus({ id: queueId, status: 'uploading', progress: 0 }));

      const response = await api.uploadFile(
        queueItem.file, 
        queueItem.description, 
        (progress) => {
          if (progress < 100) {
            dispatch(updateQueueItemStatus({ id: queueId, status: 'uploading', progress }));
          } else {
            dispatch(updateQueueItemStatus({ id: queueId, status: 'analyzing', progress: 100 }));
          }
        }
      );

      // Upload completed successfully
      const duration = Date.now() - startTime;
      dispatch(updateQueueItemStatus({ 
        id: queueId, 
        status: 'completed', 
        progress: 100, 
        result: response 
      }));

      // Track successful upload
      analytics.trackFileUploadComplete(queueItem.file.type, duration);
      
      logger.info('File uploaded successfully', { fileId: response.id, queueId });
      return { queueId, result: response };

    } catch (error: any) {
      logger.error('File upload failed', { error, queueId });
      
      // Extract meaningful error message
      let errorMessage = 'Upload failed';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout - File analysis took too long. Please try again.';
      } else if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => {
            if (err.msg) return err.msg;
            return `${err.loc?.join('.')} error`;
          });
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(updateQueueItemStatus({ 
        id: queueId, 
        status: 'failed', 
        progress: 0, 
        error: errorMessage 
      }));

      // Track failed upload
      analytics.trackFileUploadFailed(queueItem.file.type, errorMessage);

      throw error;
    }
  }
);

export const fetchFiles = createAsyncThunk(
  'upload/fetchFiles',
  async ({ skip = 0, limit = 20 }: { skip?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await api.getFiles(skip, limit);
      return response;
    } catch (error: any) {
      logger.error('Failed to fetch files', error);
      
      let errorMessage = 'Failed to fetch files';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteFile = createAsyncThunk(
  'upload/deleteFile',
  async (fileId: string, { rejectWithValue }) => {
    try {
      await api.deleteFile(fileId);
      logger.info('File deleted successfully', { fileId });
      return fileId;
    } catch (error: any) {
      logger.error('Failed to delete file', error);
      
      let errorMessage = 'Failed to delete file';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || 'Unknown error');
          errorMessage = errorMessages.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeQueueItem = createAsyncThunk(
  'upload/removeQueueItem',
  async (queueId: string) => {
    return queueId;
  }
);

export const retryQueueItem = createAsyncThunk(
  'upload/retryQueueItem',
  async (queueId: string, { dispatch }) => {
    // Reset status and retry
    dispatch(updateQueueItemStatus({ id: queueId, status: 'pending', progress: 0, error: undefined }));
    dispatch(processQueueItem(queueId));
    return queueId;
  }
);

// Slice
const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    addQueueItem: (state, action: PayloadAction<UploadQueueItem>) => {
      state.uploadQueue.push(action.payload);
    },
    updateQueueItemStatus: (state, action: PayloadAction<{
      id: string;
      status?: UploadQueueItem['status'];
      progress?: number;
      error?: string;
      result?: UploadedFile;
    }>) => {
      const item = state.uploadQueue.find(item => item.id === action.payload.id);
      if (item) {
        if (action.payload.status !== undefined) item.status = action.payload.status;
        if (action.payload.progress !== undefined) item.progress = action.payload.progress;
        if (action.payload.error !== undefined) item.error = action.payload.error;
        if (action.payload.result !== undefined) item.result = action.payload.result;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCompletedItems: (state) => {
      state.uploadQueue = state.uploadQueue.filter(item => item.status !== 'completed');
    },
  },
  extraReducers: (builder) => {
    // Process queue item
    builder
      .addCase(processQueueItem.fulfilled, (state, action) => {
        // Add completed file to files array
        if (action.payload.result) {
          state.files.unshift(action.payload.result);
        }
      });

    // Fetch files
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.files = action.payload.items;
        state.error = null;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete file
    builder
      .addCase(deleteFile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.files = state.files.filter(file => file.id.toString() !== action.payload);
        state.error = null;
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Remove queue item
    builder
      .addCase(removeQueueItem.fulfilled, (state, action) => {
        state.uploadQueue = state.uploadQueue.filter(item => item.id !== action.payload);
      });
  },
});

export const { 
  addQueueItem, 
  updateQueueItemStatus, 
  clearError, 
  clearCompletedItems 
} = uploadSlice.actions;

// Selectors
export const selectFiles = (state: RootState) => state.upload.files;
export const selectUploadQueue = (state: RootState) => state.upload.uploadQueue;
export const selectIsLoading = (state: RootState) => state.upload.isLoading;
export const selectError = (state: RootState) => state.upload.error;
export const selectActiveUploads = (state: RootState) => 
  state.upload.uploadQueue.filter(item => 
    item.status === 'pending' || item.status === 'uploading' || item.status === 'analyzing'
  );
export const selectCompletedUploads = (state: RootState) => 
  state.upload.uploadQueue.filter(item => item.status === 'completed');
export const selectFailedUploads = (state: RootState) => 
  state.upload.uploadQueue.filter(item => item.status === 'failed');

export default uploadSlice.reducer; 